import { LoaderComponent } from "@iiht/components";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import OidcAuthService from "../../../config/OidcAuthService";
import { applicationURL, translator } from "../../../constants/actionTypes";
import { statusCode } from "../../../constants/apis/apiStatus";
import { authTypes } from "../../../constants/componentConstants";
import { ROUTES } from "../../../constants/routesConstants";
import { getLoginDetailsRequest, verifyDirectTenant } from "../../../store/actions";
import { localStorageHelper } from "../../../utils";
import { directLoginStorage } from "../../../utils/storageHelper";
import NotFoundPage from "../NotFound";

const LoginRedirect = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { loginDetails } = useSelector(({ userAuthentication }) => userAuthentication);
  const { invalidTenantUrl } = useSelector(({ userAuthentication }) => userAuthentication);

  const [showLoader, setShowLoader] = useState(false);
  const [tenantUrlError, setTenantUrlError] = useState(false);
  const [data, setData] = useState({});

  const getTenantUrl = () => {
    return window.location.pathname === "/" ? "" : window.location.pathname;
  };

  const getEnv = () => {
    const hostname = window.location.hostname;

    if (hostname.includes("dev")) {
      return "https://dev.makemylabs.in";
    } else if (hostname.includes("qa")) {
      return "https://qa.makemylabs.in";
    } else if (hostname.includes("perf")) {
      return "https://perf.makemylabs.in";
    } else if (hostname.includes("uat")) {
      return "https://uat.makemylabs.in";
    } else if (hostname.includes("laas")) {
      return "https://laas.makemylabs.in";
    } else if (hostname.includes("stag")) {
      return "https://stage.makemylabs.in";
    } else if (hostname.includes("hotfix")) {
      return "https://hotfix.makemylabs.in";
    } else if (hostname.includes("localhost")) {
      return "https://dev.makemylabs.in";
    } else {
      return "https://laas.makemylabs.in";
    }
  };

  // stop loader once component unmount
  useEffect(() => {
    return () => {
      setShowLoader(false);
    };
  }, []);

  //set tenant details in local storage
  const setTenantDetails = (data) => {
    localStorageHelper.set("auth_type", data?.auth_type);
    localStorageHelper.set("isAutoOnBoarding", data?.is_auto_onboarding);
    localStorageHelper.set("localTenantId", data?.localTenantId);
    localStorageHelper.set("auth_url", data?.auth_url);

    if (data?.workspace_id) {
      localStorageHelper.set("workspaceId", data?.workspace_id);
    }
  };

  const dispatchTenantDetails = () => {
    setShowLoader(true);
    let url = directLoginStorage.get("protectedUrl") ? window.location.pathname.split("/").slice(0, 2).join("/") : getTenantUrl();
    if (window.location.pathname.includes("/sso/hexaware")) {
      dispatch(
        getLoginDetailsRequest({
          tenant_url: `${getEnv()}/hexaware`,
        })
      );
      return;
    }
    localStorageHelper.set("tenantUrl", url);
    const redirectUrl = directLoginStorage.get("protectedUrl")
      ? window?.document?.URL.includes("localhost")
        ? `${translator.baseURL}${url}`
        : window.document.URL.split("/").slice(0, 4).join("/")
      : "";
    dispatch(
      getLoginDetailsRequest({
        tenant_url: redirectUrl
          ? redirectUrl
          : window.location.pathname === "/"
            ? applicationURL
            : window?.document?.URL.includes("localhost")
              ? `${translator.baseURL}${window.location.pathname}`
              : window.document.URL,
      })
    );
  };

  useEffect(() => {
    const queryParams = window.location.href;
    const url = new URL(queryParams);
    const directLoginURLParams = url.search.substring(1);
    const pathnameParts = url.pathname.split("/");
    const tenantName = pathnameParts[1];

    const tenant_url = `${getEnv()}/${tenantName}`;
    setData({ directLoginURLParams, tenantName, tenant_url });
    directLoginStorage.set("directLoginURLParams", directLoginURLParams);
    if (directLoginURLParams) {
      setShowLoader(true);
    }
    return () => {
      setShowLoader(false);
    };
  }, [window.location.hostname]);

  useEffect(() => {
    if (localStorage.getItem("accessToken") && window.location.href.includes("odl")) {
      setShowLoader(true);
      directLoginStorage.set("isDemandLogin", true);
      let skuId = window.document.URL.split("/odl/")?.[1];
      let demandSkuId = window.document.URL.split("/odl/")?.[1]?.split("?")?.[0];
      let skuIDSet = directLoginStorage.get('directLoginURLParams') ? demandSkuId : skuId;
      directLoginStorage.set("skuId", skuIDSet);
      let demandLoginUrl = window.document.URL.replace(window?.document?.URL.slice(window?.document?.URL.indexOf("/odl/")), "");
      let url = demandLoginUrl?.includes("localhost")
        ? demandLoginUrl?.replace(translator.localhost3000, `${translator.baseURL}`)
        : demandLoginUrl;

      //tenant url for demand login
      directLoginStorage.set("demandLoginUrl", url);
      history.push({
        pathname: ROUTES.VM_CONNECT,
      });
      return;
    }

    if (window.location.href.includes("/userMachineCatalog")) {
      const redirectUrl = ["", window.location.pathname.split("/").slice(2).join("/")].join("/");
      directLoginStorage.set("protectedUrl", redirectUrl);
      if (localStorage.getItem("accessToken")) {
        history.push({
          pathname: redirectUrl,
        });
      }
    }

    //block for workspace url validation
    if (window.location.href.includes("wsm") && !localStorage.getItem("wsmAccessToken") && !sessionStorage.getItem("wsDetails")) {
      const queryString = window?.location?.search;
      const urlParams = new URLSearchParams(queryString);
      const invitationCode = urlParams.get("invitation_id");
      const contextVal = urlParams.get("context");
      const wsmUrl = `${translator.baseURL}${window?.location?.pathname}`;
      sessionStorage.setItem("wsDetails", true);
      let wsmAccessToken = localStorageHelper.get("wsmAccessToken");
      if (!wsmAccessToken && invitationCode) {
        history.push({
          pathname: ROUTES.WSM_LOGIN,
          state: { invitationCode, contextVal, wsmUrl },
        });
      } else {
        history.push({
          pathname: ROUTES.WSM_LOGIN,
          state: { wsmUrl },
        });
      }
    } else if (localStorage.getItem("wsmAccessToken") && sessionStorage.getItem("wsDetails")) {
      return;
    }

    //block for portal url validation
    if (!window.location.href.includes("wsm") && !localStorage.getItem("accessToken") && !sessionStorage.getItem("portDetails")) {
      //block for demand login url validation
      if (window.location.href.includes("odl")) {
        //flag for demand login
        directLoginStorage.set("isDemandLogin", true);
        let skuId = window.document.URL.split("/odl/")?.[1];
        let demandSkuId = window.document.URL.split("/odl/")?.[1]?.split("?")?.[0];
        let skuIDSet = directLoginStorage.get('directLoginURLParams') ? demandSkuId : skuId;
        directLoginStorage.set("skuId", skuIDSet);
        let demandLoginUrl = window.document.URL.replace(window?.document?.URL.slice(window?.document?.URL.indexOf("/odl/")), "");
        let url = demandLoginUrl?.includes("localhost")
          ? demandLoginUrl?.replace(translator.localhost3000, `${translator.baseURL}`)
          : demandLoginUrl;

        //tenant url for demand login
        directLoginStorage.set("demandLoginUrl", url);

        const OnDemandPayload = {
          tenant_url: url
        };

        const demandLocalUserLoginPayload = {
          encrypted_text: data?.directLoginURLParams,
          tenant_url: data?.tenant_url
        }
        const payloadLogin = (data && data?.directLoginURLParams) ? demandLocalUserLoginPayload : OnDemandPayload;

        if (Object.keys(data).length > 0) {
          dispatch(verifyDirectTenant(payloadLogin));
        }
      } else {
        dispatchTenantDetails();
      }
    } else if (localStorage.getItem("accessToken") && sessionStorage.getItem("wsDetails")) {
      return;
    }
  }, [window?.location?.origin, data]);

  useEffect(() => {
    if (loginDetails?.status === statusCode.Ok200) {
      setShowLoader(false);
      setTenantDetails(loginDetails?.data);

      //handle login page redirection
      if (window?.location.href?.includes("wsm")) {
        history.push({
          pathname: ROUTES?.WSM_LOGIN,
        });
      } else {
        if (window.location.pathname.includes("/sso/hexaware")) {
          const redirectUri = "https://laas.makemylabs.in/standBy";
          localStorageHelper.set("isHexawareSSO", true);
          localStorageHelper.set("auth_url", "https://idp-prod-iris-hexaware.azurewebsites.net");
          localStorageHelper.set("tenant_name", "hexaware");
          localStorageHelper.set("hexaware_redirect_uri", redirectUri);

          const oidc = new OidcAuthService("https://idp-prod-iris-hexaware.azurewebsites.net", "hexaware", {
            client_id: "61afd809-9c44-4e3f-b691-1d6466d35f48",
            scope: "openid profile email default-api offline_access",
            redirect_uri: redirectUri,
            acr_values: "idp:hexaware",
            usePkce: false
          });

          oidc.startAuthentication();
          return;
        }

        if (
          (directLoginStorage.get("isDemandLogin") || directLoginStorage.get("protectedUrl")) &&
          loginDetails?.data?.is_auto_onboarding &&
          loginDetails?.data?.auth_type !== authTypes.localUser
        ) {
          //code for direct login to vm-connect in case for Demand login with different auth configs
          if (loginDetails?.data?.auth_type === authTypes.azuerAD) {
            localStorageHelper.set("sessionId", loginDetails?.data?.sessionid);
            window.open(loginDetails?.data?.auth_url, "_self");
          }
          if (loginDetails?.data?.auth_type === authTypes.SAML) {
            localStorageHelper.set("sessionId", loginDetails?.data?.sessionid);
            let oidc = new OidcAuthService(loginDetails?.data?.auth_url, loginDetails?.data?.tenant_name);
            oidc.startAuthentication();
          }
        } else {
          history.push({
            pathname: ROUTES.LOGIN,
          });
        }
      }
    }
    if (invalidTenantUrl?.status === statusCode.BadRequest400) {
      setShowLoader(false);
      setTenantUrlError(true);
    }
  }, [loginDetails, invalidTenantUrl]);

  return (
    <>
      {showLoader && <LoaderComponent spinning isBackdrop />}
      {tenantUrlError && <NotFoundPage invalidTenant />}
    </>
  );
};

export default LoginRedirect;
